import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  input,
  output,
  signal,
  effect,
} from '@angular/core';
import { Router } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { Menu } from 'primeng/menu';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MenuItem } from 'primeng/api';
import { PaHttpClient } from '../../../clients/http-client';
import { GmapService } from 'src/app/shared/services/gmap.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ToastrService } from 'src/app/shared/services/toastr.service';
import { environment } from '../../../../environments/environment';
import { ServiceMapComponent } from '../../../views/service-map/service-map.component';
import { computed } from '@angular/core';
import { SERVICE_UI_MAP, ServiceMapState, ServiceMapStateService } from 'src/app/shared/components/service-option-popup/service-ui.config';
import { ActivityRefreshService } from 'src/app/shared/services/activity-refresh.service';

declare var google: any;

interface ServiceType {
  label: string;
  service_type: string;
  search_value: string;
  description: string;
  subServices?: SubServiceType[];
}

interface SubServiceType {
  label: string;
  sub_service_type: string;
  description: string;
  search_value: string;
  icon?: string;
  url?: string;
}

interface SearchResult {
  success: boolean;
  data: BusinessResult[];
  nextPageToken?: string;
  count?: number;
}

export interface BusinessResult {
  id?: string;
  google_business_place_id?: string;
  name?: string;
  types?: string[];
  formattedAddress?: string;
  location?: { latitude: number; longitude: number };
  businessStatus?: string;
  iconBackgroundColor?: string;
  displayName?: { text: string; languageCode?: string };
  primaryTypeDisplayName?: { text: string; languageCode?: string };
  primaryType?: string;
  rating?: number;
  reviewSummary?: {
    text?: {
      text?: string;
      languageCode?: string;
    };
  };
  nationalPhoneNumber?: string;
  regularOpeningHours?: {
    openNow?: boolean;
    periods?: Array<{
      open?: { day?: number; hour?: number; minute?: number };
      close?: { day?: number; hour?: number; minute?: number };
    }>;
    nextCloseTime?: string;
    nextOpenTime?: string;
    weekdayDescriptions?: string[];
  };
  priceLevel?: number;
  priceRange?: {
    startPrice?: {
      currencyCode?: string;
      units?: string;
    };
    endPrice?: {
      currencyCode?: string;
      units?: string;
    };
  };
  userRatingCount?: number;
  recommendationCount?: number;
  photos?: Array<{
    name?: string;
    image_url?: string;
    widthPx?: number;
    heightPx?: number;
    flagContentUri?: string;
    googleMapsUri?: string;
    authorAttributions?: Array<{ displayName?: string; uri?: string; photoUri?: string }>;
  }>;
  googleMapsUri?: string;
  websiteUri?: string;
  photoUrl?: string;
  image_url?: string;
  mapUrl?: string;
  mapurl?: string;
  verified?: boolean;
  accessibilityOptions?: {
    wheelchairAccessibleEntrance?: boolean;
    wheelchairAccessibleParking?: boolean;
    wheelchairAccessibleRestroom?: boolean;
    wheelchairAccessibleSeating?: boolean;
  };
  average_rating?: number;
  total_rating_count?: number;
  total_recommended?: number;
  is_recommended?: boolean;
  recommended_user_details?: Array<{
    user_id?: string;
    first_name?: string;
    last_name?: string;
    profile_picture?: string;
  }>;
  reviews?: Array<{
    name?: string;
    relativePublishTimeDescription?: string;
    rating?: number;
    text?: {
      text?: string;
      languageCode?: string;
    };
    authorAttribution?: {
      displayName?: string;
      uri?: string;
      photoUri?: string;
    };
    publishTime?: string;
    [key: string]: any;
  }>;
  googleMapOverviewImageLink?: string;
  has_feedback?: boolean;
}

interface PricingData {
  pricing_id: string;
  service_type: string;
  service_sub_type: string;
  nickname: string;
  per_lbs: number;
  minimum_fee: number;
  delivery_fee: number;
  one_day_rush_fee: number;
  sales_tax: number;
  is_default: boolean;
  pricing_items?: any[];
}

interface ServiceProvider {
  user_id: string | number;
  first_name?: string;
  last_name?: string;
  service_provider_name?: string;
  profile_picture?: string;
  profile_picture_moderation_status?: string | null;
  documents?: { url?: string }[];
  address?: string;
  city?: string;
  state?: string | { short_name?: string; state_name?: string };
  zipcode?: string;
  is_verified?: boolean;
  service_type?: string;
  role?: string;
  addresses?: { address?: string; city?: string; zipcode?: string; address_type?: string; location_lat_long?: string; state?: { short_name?: string; state_name?: string } }[];
}

function getDefaultPickupDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

@Component({
  selector: 'pa-create-requests-popup',
  imports: [DialogModule, Menu, IconFieldModule, InputIconModule, InputTextModule],
  templateUrl: './requests-create.component.html',
  styleUrls: ['./requests-create.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PaHttpClient],
  host: { '(document:click)': 'onDocClick($event)',
    '(window:resize)': 'onWindowResize()',
    '(window:orientationchange)': 'onWindowResize()' },
})
export class CreateRequestPopupComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mapContainer') private mapContainer!: ElementRef<HTMLDivElement>;
  private youreHereOverlay: any = null;
  private pickupLocationOverlay: any = null;
  readonly hasSearched = signal(false);
  readonly skipToLaundry = input<boolean>(false);
  readonly editRequestId = input<string | null>(null);
  readonly editRequestData = input<any>(null);

  readonly close = output<void>();
  requestComplete = output<any>();
  readonly requestService = output<void>();

  readonly isEditMode = computed(() => !!this.editRequestId());
  @ViewChild('currentMenu') currentMenu!: Menu;

  private readonly httpClient = inject(PaHttpClient);
  private readonly gmapService = inject(GmapService);
  private readonly router = inject(Router);
  private readonly mapStateService = inject(ServiceMapStateService);
  private readonly authService = inject(AuthService);
  private readonly toastr = inject(ToastrService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly activityRefresh = inject(ActivityRefreshService);
readonly isReviewStep = computed(() => this.currentStep() === 'review_details');
readonly isMainStep = computed(() => 
  !this.isReviewStep() 
);
  readonly currentUser = this.authService.getUser();
  readonly mapState = computed<ServiceMapState | null>(() => {
    const center = this.searchCenter();
    if (!center) return null;
    return {
      center,
      realUserLocation: this.realUserLocation() || undefined,
      businessResults: this.businessResults(),
      markers: [
        ...(this.realUserLocation() ? [{ lat: this.realUserLocation()!.lat, lng: this.realUserLocation()!.lng, title: "You're here", type: 'user' }] : []),
        ...(this.searchCenter() ? [{ lat: this.searchCenter()!.lat, lng: this.searchCenter()!.lng, title: "Search Center", type: 'search' }] : [])
      ],
      zoom: 13,
      searchQuery: this.addressInput(),
      resultsCount: this.resultsCount()
    };
  });

  private readonly LAUNDRY_SUBTYPES: SubServiceType[] = [
    {
      label: 'Wash & Fold',
      sub_service_type: '1',
      description: 'The perfect service for your daily laundry needs.',
      search_value: 'wash_and_fold',
      url: 'assets/icons/washfold-icon.svg',
    },
    {
      label: 'Dry Clean',
      sub_service_type: '2',
      description: 'Ideal for items you want cleaned, pressed and returned on a hanger.',
      search_value: 'dry_clean',
      url: 'assets/icons/dryclean-icon.svg',
    },
    {
      label: 'Hang Dry',
      sub_service_type: '3',
      description: 'Perfect for delicates, activewear, or any non-dryer items.',
      search_value: 'hang_dry',
      url: 'assets/icons/hangdry-icon.svg',
    },
    {
      label: 'Washing Machine',
      sub_service_type: '5',
      description: 'No hassles. Set your wash day and schedule your use of the washing machine.',
      search_value: 'washing_machine',
      url: 'assets/icons/washmachine-icon.svg',
    },
  ];

  readonly currentStep = signal<'type' | 'subtype' | 'request_type' | 'pickup_datetime' | 'review_details' | 'pickup_location'>('type');

  isMobileView = signal<boolean>(false);
  dialogStyle: Record<string, string> = { width: '869px', maxWidth: '95vw', margin: '1rem' };

  onWindowResize(): void {
    this.isMobileView.set(window.innerWidth < 768);
  }

  private setStep(step: 'type' | 'subtype' | 'request_type' | 'pickup_datetime' | 'review_details' | 'pickup_location'): void {
    this.currentStep.set(step);
    this.updateDialogStyle();
  }

  goToStep(step: 'type' | 'subtype' | 'request_type' | 'pickup_datetime' | 'review_details' | 'pickup_location'): void {
    this.setStep(step);
  }
private updateDialogStyle(): void {
  const mobile = this.isMobileView();
  if (this.currentStep() === 'review_details') {
    this.dialogStyle = { 
      width: mobile ? '95vw' : '532px', 
      maxWidth: '95vw', 
      margin: '1rem' 
    };
  }  else {
    this.dialogStyle = { 
      width: mobile ? '95vw' : '869px', 
      maxWidth: '95vw', 
      margin: '1rem' 
    };
  }
  this.cdr.markForCheck();
  this.cdr.detectChanges();
}
 
  readonly selectedPickupAddress = signal<string>('');

  // Review Details step
  readonly pricingData = signal<PricingData | null>(null);
  readonly isLoadingPricing = signal(false);
  readonly showPricingBreakdown = signal(true);
  readonly CONVENIENT_FEE = 3;
  readonly selectedRequestType = signal<'on_demand' | 'selected_shop' | null>(null);

  // Provider popup
  readonly showProviderPopup = signal(false);
  readonly providerList = signal<ServiceProvider[]>([]);
  readonly isLoadingProviders = signal(false);
  readonly providerSearch = signal('');
  readonly providerTab = signal<'all' | 'preferred'>('all');
  readonly selectedProvider = signal<ServiceProvider | null>(null);

  readonly filteredProviders = computed(() => {
    const q = this.providerSearch().toLowerCase();
    return this.providerList().filter(p => {
      const name = (p.service_provider_name || `${p.first_name ?? ''} ${p.last_name ?? ''}`).toLowerCase();
      return !q || name.includes(q);
    });
  });

  openProviderPopup(): void {
    this.showProviderPopup.set(true);
    if (!this.providerList().length) this.loadProviders();
  }

  private loadProviders(): void {
    this.isLoadingProviders.set(true);
    const serviceType = this.selectedServiceType()?.service_type ?? 'laundry';
    this.httpClient.getWithParams('user/list_service_providers', { fetch: serviceType }).subscribe({
      next: (res: any) => {
        this.providerList.set(res?.data ?? []);
        this.isLoadingProviders.set(false);
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoadingProviders.set(false);
        this.cdr.markForCheck();
      },
    });
  }

  selectProvider(provider: ServiceProvider): void {
    this.selectedProvider.set(provider);
    this.showProviderPopup.set(false);
    if (this.selectedRequestType() === 'selected_shop' && this.currentStep() === 'request_type') {
      this.setStep('pickup_datetime');
    }
  }

  providerAvatar(p: ServiceProvider): string | null {
    const url = p.profile_picture || p.documents?.[0]?.url;
    return url && url.trim() ? url : null;
  }

  isPictureBlocked(status: string | null | undefined): boolean {
    return status === 'flagged' || status === 'blocked';
  }

  getInitials(p: ServiceProvider): string {
    const name = this.providerDisplayName(p);
    if (!name || name === '—') return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  providerDisplayName(p: ServiceProvider): string {
    return p.service_provider_name || `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim() || '—';
  }

  providerLocation(p: ServiceProvider): string {
    const addr = p.addresses?.find((a) => a.address_type === 'physical') ?? p.addresses?.[0];
    const stateObj = addr?.state ?? p.state;
    const city = addr?.city || p.city || '';
    const state = typeof stateObj === 'string'
      ? stateObj
      : (stateObj?.short_name || stateObj?.state_name || '');
    const zip = addr?.zipcode || p.zipcode || '';
    return [city, state, zip].filter(Boolean).join(', ');
  }

  // Submit
  readonly isSubmitting = signal(false);
  readonly showSuccessPopup = signal(false);

  // Gallery
  readonly galleryPreviews = signal<{ preview: string; file: File }[]>([]);

  // Pickup Date & Time step
  readonly selectedDate = signal<string>(getDefaultPickupDate());
  readonly selectedHour = signal<string>(new Date().getHours().toString().padStart(2, '0'));
  readonly meetupDuration = signal<string>(new Date().getMinutes().toString().padStart(2, '0'));
  readonly showHourDropdown = signal(false);
  readonly showMinuteDropdown = signal(false);

  minutesList: string[] = Array.from({ length: 60 }, (_, x) => x.toString().padStart(2, '0'));
  hoursList: { value: string; title: string }[] = Array.from({ length: 24 }, (_, x: number) => {
    const x_string = x < 10 ? '0' + x.toString() : x.toString();
    return { value: x_string, title: `${x_string} (${x % 12 || 12} ${x < 12 ? 'AM' : 'PM'})` };
  });
  readonly serviceTypes = signal<ServiceType[]>([]);
  readonly selectedServiceType = signal<ServiceType | null>(null);
  readonly availableSubTypes = signal<SubServiceType[]>([]);
  readonly selectedSubType = signal<SubServiceType | null>(null);
  readonly addressInput = signal('');
  readonly isLoadingServiceTypes = signal(false);
  readonly isSearching = signal(false);
  readonly searchCenter = signal<{ lat: number; lng: number } | null>(null);
  readonly realUserLocation = signal<{ lat: number; lng: number } | null>(null);
  readonly businessResults = signal<BusinessResult[]>([]);
  readonly resultsCount = signal(0);
  readonly showAutocomplete = signal(false);
  readonly autocompleteResults = signal<any[]>([]);

  private map: any = null;
  private markers: any[] = [];
  private autocompleteService: any = null;
  private CardOverlay: any = null;
  private mapInitialized = false;
  currentMenuItems: MenuItem[] = [
    {
      label: 'Call',
    }, {
      label: 'Visit Website'
    },
    {
      label: 'Book an Appointment',
    },]
  ngOnInit(): void {
    this.isMobileView.set(window.innerWidth < 768);
    this.getUserLocation();
    this.loadServiceTypes();

    const editData = this.editRequestData();
    if (editData) {
      this.populateFromExisting(editData);
      return;
    }

    const userAddr = this.getUserAddress();
    if (userAddr) {
      this.selectedPickupAddress.set(userAddr);
      this.addressInput.set(userAddr);
    }
    const userLatLong = this.currentUser?.addresses?.[0]?.location_lat_long as string | undefined;
    if (userLatLong) {
      const [latStr, lngStr] = userLatLong.split(',');
      const lat = parseFloat(latStr);
      const lng = parseFloat(lngStr);
      if (!isNaN(lat) && !isNaN(lng)) {
        this.searchCenter.set({ lat, lng });
      }
    }
    if (this.skipToLaundry()) {
      const laundryType: ServiceType = {
        label: 'Laundry',
        service_type: 'laundry',
        search_value: 'laundry',
        description: 'Laundry services',
      };
      this.selectedServiceType.set(laundryType);
      this.availableSubTypes.set(this.LAUNDRY_SUBTYPES);
      this.currentStep.set('subtype');
    }
  }

  private populateFromExisting(d: any): void {
    // Service type
    const serviceType = d.service_type ?? '';
    const serviceTypeLabel = (serviceType.charAt(0).toUpperCase() + serviceType.slice(1)).replace(/_/g, ' ');
    this.selectedServiceType.set({
      label: serviceTypeLabel,
      service_type: serviceType,
      search_value: serviceType,
      description: '',
    });

    // Sub-type 
    const subTypeVal = d.service_sub_type ?? '';
    if (serviceType === 'laundry') {
      this.availableSubTypes.set(this.LAUNDRY_SUBTYPES);
      const found = this.LAUNDRY_SUBTYPES.find(s => s.search_value === subTypeVal);
      this.selectedSubType.set(found ?? {
        label: subTypeVal,
        sub_service_type: subTypeVal,
        search_value: subTypeVal,
        description: '',
      });
      } else if (subTypeVal) {
      this.selectedSubType.set({
        label: subTypeVal.replace(/_/g, ' '),
        sub_service_type: subTypeVal,
        search_value: subTypeVal,
        description: '',
      });
    }

    // Request type
    const reqType = d.request_type === 'selected_shop' ? 'selected_shop' : 'on_demand';
    this.selectedRequestType.set(reqType as 'on_demand' | 'selected_shop');

    // Pickup date & time
    if (d.pickup_date) this.selectedDate.set(d.pickup_date);
    if (d.pickup_time) {
      const [hh, mm] = d.pickup_time.split(':');
      this.selectedHour.set(hh?.padStart(2, '0') ?? '09');
      this.meetupDuration.set(mm?.padStart(2, '0') ?? '00');
    }

    // Pickup location
    if (d.pickup_location) {
      this.selectedPickupAddress.set(d.pickup_location);
      this.addressInput.set(d.pickup_location);
    }
    if (d.pickup_location_lat_long) {
      const [latStr, lngStr] = d.pickup_location_lat_long.split(',');
      const lat = parseFloat(latStr);
      const lng = parseFloat(lngStr);
      if (!isNaN(lat) && !isNaN(lng)) this.searchCenter.set({ lat, lng });
    }

    // Provider
    if (d.provider_detail) {
      this.selectedProvider.set(d.provider_detail);
    }

    // Reconstruct pricing from saved fee fields
    if (d.minimum_service_fee != null) {
      this.pricingData.set({
        pricing_id: String(d.pricing_id ?? ''),
        service_type: serviceType,
        service_sub_type: subTypeVal,
        nickname: '',
        per_lbs: 0,
        minimum_fee: Number(d.minimum_service_fee ?? 0),
        delivery_fee: Number(d.delivery_fee ?? 0),
        one_day_rush_fee: Number(d.one_day_rush_fee ?? 0),
        sales_tax: Number(d.sales_tax ?? 0),
        is_default: false,
      });
    } else {
      this.loadPricing();
    }

    // Jump straight to review
    this.currentStep.set('review_details');
    this.updateDialogStyle();
  }

  ngAfterViewInit(): void { }

  ngOnDestroy(): void {
    this.clearMapMarkers();
  }

  onBack(): void {
    const step = this.currentStep();
    if (step === 'pickup_location') {
      this.teardownMap();
      this.setStep('review_details');
    } else if (step === 'review_details') {
      this.setStep('pickup_datetime');
    } else if (step === 'pickup_datetime') {
      this.setStep('request_type');
    } else if (step === 'request_type') {
      if (this.selectedSubType()) {
        this.setStep('subtype');
      } else {
        this.setStep('type');
      }
    } else if (step === 'subtype') {
      this.setStep('type');
    } else if (step === 'type') {
      this.close.emit();
      this.resetSearchForm();
    } else {
      this.close.emit();
    }
  }

  onCloseDialog(): void {
    this.close.emit();
  }

  onViewList(): void {
    const queryParams: any = {};

    if (this.selectedServiceType()?.label) {
      queryParams['service_name'] = this.selectedServiceType()?.label;
    }

    const location = this.addressInput().trim();
    if (location) {
      queryParams['location'] = location;
    }

    if (this.businessResults().length > 0) {
      // Map results with photoUrls for the sidebar map
      const resultsWithPhotos = this.businessResults().map(b => ({
        ...b,
        photoUrl: this.getBusinessPhotoUrl(b)
      }));

      sessionStorage.setItem('serviceSearchResults', JSON.stringify(resultsWithPhotos));
      sessionStorage.setItem('serviceSearchCount', this.resultsCount().toString());
      if (this.selectedServiceType()?.label) {
        sessionStorage.setItem('serviceSearchName', this.selectedServiceType()!.label);
      }
      if (location) {
        sessionStorage.setItem('serviceSearchLocation', location);
      } else {
        sessionStorage.removeItem('serviceSearchLocation');
      }

      // Prepare Map state for sidebar, including search locations
      const markers: any[] = resultsWithPhotos.map(b => ({
        lat: Number(b.location?.latitude ?? 0),
        lng: Number(b.location?.longitude ?? 0),
        title: this.getBusinessDisplayName(b),
        placeId: b.id
      })).filter(m => Number.isFinite(m.lat) && Number.isFinite(m.lng) && m.lat !== 0 && m.lng !== 0);

      // Add "You're here" marker if it exists
      if (this.realUserLocation()) {
        markers.push({
          lat: this.realUserLocation()!.lat,
          lng: this.realUserLocation()!.lng,
          title: "You're here",
          type: 'user'
        });
      }

      // Add Search Center marker if it differs from user location
      if (this.searchCenter()) {
        markers.push({
          lat: this.searchCenter()!.lat,
          lng: this.searchCenter()!.lng,
          title: "Search Location",
          type: 'search'
        });
      }

      sessionStorage.setItem('serviceSearchMarkers', JSON.stringify(markers));

      this.mapStateService.setMapState({
        center: this.searchCenter() ?? this.realUserLocation() ?? { lat: markers[0]?.lat, lng: markers[0]?.lng },
        realUserLocation: this.realUserLocation() ?? undefined,
        markers,
        zoom: 13,
        searchQuery: location || this.selectedServiceType()?.label,
        serviceName: this.selectedServiceType()?.label,
        locationName: location,
        resultsCount: this.resultsCount(),
        businessResults: resultsWithPhotos
      });
      this.mapStateService.activateSidebarMap();
    }

    this.router.navigate(['/services'], { queryParams: { ...queryParams, activeTab: 'service-providers' } });
    this.close.emit();
  }


  loadServiceTypes(): void {
    if (this.serviceTypes().length > 0) return;
    this.isLoadingServiceTypes.set(true);
    this.httpClient
      .get<{ success: boolean; data: ServiceType[] }>('search-business-places/service-type-list')
      .subscribe({
        next: (response) => {
          this.isLoadingServiceTypes.set(false);
          if (response.success && Array.isArray(response.data)) {
            this.serviceTypes.set(response.data);
          }
        },
        error: () => this.isLoadingServiceTypes.set(false),
      });
  }

  onServiceTypeClick(type: ServiceType): void {
    this.selectedServiceType.set(type);
    this.selectedSubType.set(null);
    this.selectedRequestType.set(null);
    this.selectedProvider.set(null);
    this.providerList.set([]);
    this.pricingData.set(null);
    const isLaundry = type.service_type === 'laundry' || type.label.toLowerCase().includes('laundry');
    if (isLaundry) {
      this.availableSubTypes.set(this.LAUNDRY_SUBTYPES);
      this.setStep('subtype');
    } else if (type.subServices?.length) {
      this.availableSubTypes.set(type.subServices);
      this.setStep('subtype');
    } else {
      this.availableSubTypes.set([]);
      this.setStep('request_type');
    }
  }

  onSubTypeClick(subType: SubServiceType): void {
    this.selectedSubType.set(subType);
    this.setStep('request_type');
  }

  onRequestTypeSelect(type: 'on_demand' | 'selected_shop'): void {
    this.selectedRequestType.set(type);
    this.providerList.set([]);
    this.pricingData.set(null);
    if (type === 'on_demand') {
      this.setStep('pickup_datetime');
    } else {
      this.showProviderPopup.set(true);
      if (!this.providerList().length) this.loadProviders();
    }
  }

  // ── Pickup Date & Time helpers ──────────────────────────────
  onDocClick(e: MouseEvent): void {
    if (this.showHourDropdown() || this.showMinuteDropdown()) {
      const target = e.target as HTMLElement;
      if (!target.closest('.time-dropdown-wrap')) {
        this.showHourDropdown.set(false);
        this.showMinuteDropdown.set(false);
      }
    }
  }

  toggleHourDropdown(e: MouseEvent): void {
    e.stopPropagation();
    this.showHourDropdown.set(!this.showHourDropdown());
    this.showMinuteDropdown.set(false);
  }

  toggleMinuteDropdown(e: MouseEvent): void {
    e.stopPropagation();
    this.showMinuteDropdown.set(!this.showMinuteDropdown());
    this.showHourDropdown.set(false);
  }

  selectHour(index: number): void {
    this.selectedHour.set(this.hoursList[index].value);
    this.showHourDropdown.set(false);
  }

  selectMinute(index: number): void {
    this.meetupDuration.set(this.minutesList[index]);
    this.showMinuteDropdown.set(false);
  }

  getHourLabel(): string {
    const h = parseInt(this.selectedHour());
    const ampm = h < 12 ? 'AM' : 'PM';
    const displayH = h % 12 || 12;
    const padH = displayH < 10 ? '0' + displayH : displayH.toString();
    return `${padH} ${ampm}`;
  }

  getFormattedDate(): string {
    const dateStr = this.selectedDate();
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[date.getMonth()]} ${date.getDate().toString().padStart(2, '0')}`;
  }

  getDayOfWeek(): string {
    const dateStr = this.selectedDate();
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    return days[date.getDay()];
  }

  openDatePicker(dateInput: HTMLInputElement): void {
    try {
      dateInput.showPicker();
    } catch {
      dateInput.click();
    }
  }

  onDateChange(event: Event): void {
    this.selectedDate.set((event.target as HTMLInputElement).value);
  }

  onPickupNext(): void {
    this.setStep('review_details');
    this.loadPricing();
  }

  // ── Review Details helpers ──────────────────────────────────
  loadPricing(): void {
    const serviceType = this.selectedServiceType()?.service_type ?? '';
    const subType = this.selectedSubType()?.search_value ?? '';
    if (!serviceType || !subType) return;
    this.isLoadingPricing.set(true);
    this.pricingData.set(null);
    this.httpClient
      .getWithParams<{ success: boolean; data: PricingData }>(
        'pricing/items_pricing',
        { service_type: serviceType, service_sub_type: subType }
      )
      .subscribe({
        next: (res) => {
          this.isLoadingPricing.set(false);
          if (res.success) this.pricingData.set(res.data);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoadingPricing.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  getPickupDateTimeLabel(): string {
    const date = this.getFormattedDate();
    const day = this.getDayOfWeek();
    const h = parseInt(this.selectedHour());
    const mins = this.meetupDuration();
    const ampm = h < 12 ? 'AM' : 'PM';
    const displayH = h % 12 || 12;
    return `${date}, ${day} ${displayH}:${mins}${ampm}`;
  }

  getPricingSubtotal(): number {
    const p = this.pricingData();
    if (!p) return 0;
    return p.minimum_fee + p.delivery_fee;
  }

  getPricingTax(): number {
    const p = this.pricingData();
    if (!p) return 0;
    return parseFloat(((p.sales_tax / 100) * this.getPricingSubtotal()).toFixed(2));
  }

  getPricingTotal(): number {
    return this.getPricingSubtotal() + this.getPricingTax() + this.CONVENIENT_FEE;
  }

  formatCurrency(val: number): string {
    return '$' + val.toFixed(2);
  }

  getUserFullName(): string {
    const u = this.currentUser;
    if (!u) return '';
    return `${u.first_name || ''} ${u.last_name || ''}`.trim();
  }

  getUserAvatar(): string {
    return this.currentUser?.profile_picture || 'assets/icons/default-profile.png';
  }

  getUserAddress(): string {
    const addresses: any[] = this.currentUser?.addresses;
    if (!addresses?.length) return '';
    const primary = addresses[0];
    const parts = [primary.address, primary.city, primary.state?.short_name ?? '', primary.zipcode]
      .filter(Boolean);
    return parts.join(', ');
  }

  onCreateRequest(): void {
    if (this.isSubmitting()) return;
    if (!this.pricingData()) {
      this.toastr.showError('Pricing is unavailable for this request. Please try again.');
      return;
    }
    this.isSubmitting.set(true);

    const u = this.currentUser;
    const p = this.pricingData();
    const loc = this.searchCenter() ?? this.realUserLocation();
    const latLong = loc ? `${loc.lat},${loc.lng}` : '';
    const pickupLocation = this.selectedPickupAddress();
    const pickupType = this.selectedRequestType() === 'selected_shop' ? 'scheduled' : 'on_demand';
    const providerAddr = this.selectedProvider()?.addresses?.[0];
    const dropoffLocation = providerAddr
      ? [providerAddr.address, providerAddr.city, providerAddr.state?.short_name, providerAddr.zipcode].filter(Boolean).join(', ')
      : pickupLocation;
    const dropoffLatLong = providerAddr?.location_lat_long ?? latLong;
    const payload = {
      service_type: this.selectedServiceType()?.service_type ?? '',
      service_sub_type: this.selectedSubType()?.search_value ?? '',
      request_type: this.selectedRequestType() ?? 'on_demand',
      pickup_type: pickupType,
      pickup_date: this.selectedDate(),
      pickup_time: `${this.selectedHour()}:${this.meetupDuration()}:00`,
      pickup_location: pickupLocation,
      pickup_location_lat_long: latLong,
      dropoff_location: dropoffLocation,
      dropoff_location_lat_long: dropoffLatLong,
      payment_type: 'pay_now',
      status: 'accepted',
      tenant_id: String(u?.user_id ?? ''),
      tenant_name: `${u?.first_name ?? ''} ${u?.last_name ?? ''}`.trim(),
      tenant_email: u?.email ?? '',
      service_provider_id: String(this.selectedProvider()?.user_id ?? ''),
      requested_by: {
        email: u?.email ?? '',
        address: pickupLocation,
        full_name: `${u?.first_name ?? ''} ${u?.last_name ?? ''}`.trim(),
        work_number: (u as any)?.mobile_number ?? '',
      },
      pricing_id: p?.pricing_id ?? '',
      minimum_service_fee: p?.minimum_fee ?? 0,
      delivery_fee: p?.delivery_fee ?? 0,
      one_day_rush_fee: p?.one_day_rush_fee ?? 0,
      sales_tax: p?.sales_tax ?? 0,
      technology_fee: this.CONVENIENT_FEE,
      discount: 0,
      total_price: this.getPricingTotal(),
    };

    const editId = this.editRequestId();
    const request$ = editId
      ? this.httpClient.put(`request/update/${editId}`, payload)
      : this.httpClient.post('request/add', payload);

    const successMsg = editId ? 'Request updated successfully.' : undefined;

    request$.subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.activityRefresh.triggerRefresh();
        if (successMsg) this.toastr.showSuccess(successMsg);
        this.showSuccessPopup.set(true);
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.isSubmitting.set(false);
        const action = editId ? 'update' : 'create';
        const msg = err?.error?.message || err?.message || `Failed to ${action} request. Please try again.`;
        this.toastr.showError(msg);
        this.cdr.markForCheck();
      },
    });
  }

  onSuccessOk(): void {
    this.showSuccessPopup.set(false);
    this.requestComplete.emit({ success: true });
    this.close.emit();
  }

  onGalleryFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const MAX = 10;
    const remaining = MAX - this.galleryPreviews().length;
    let queued = 0;
    for (const file of Array.from(input.files)) {
      if (queued >= remaining) break;
      if (!file.type.startsWith('image/')) continue;
      queued++;
     const reader = new FileReader();
      reader.onload = () => {
        this.galleryPreviews.update(prev => [...prev, { preview: reader.result as string, file }]);
        this.cdr.markForCheck();
      };
      reader.readAsDataURL(file);
    }
    input.value = '';
  }

  removeGalleryImage(index: number): void {
    this.galleryPreviews.update(prev => prev.filter((_, i) => i !== index));
  }

  goToPickupLocation(): void {
    this.teardownMap();
    this.setStep('pickup_location');
    this.cdr.detectChanges();
    setTimeout(() => this.initMap(), 50);
  }

  onPickupLocationNext(): void {
    const addr = this.addressInput().trim();
    if (addr) this.selectedPickupAddress.set(addr);
    this.teardownMap();
    this.setStep('review_details');
  }

  onRemoveServiceTag(): void {
    this.selectedServiceType.set(null);
    this.selectedSubType.set(null);
    this.availableSubTypes.set([]);
    this.businessResults.set([]);
    this.resultsCount.set(0);
    this.clearMapMarkers();
  }

  onRemoveSubTypeTag(): void {
    this.selectedSubType.set(null);
  }

  getUserLocation(): void {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        this.realUserLocation.set(loc);
        if (!this.searchCenter()) {
          this.searchCenter.set(loc);
        }
        if (this.mapInitialized) {
          this.buildYoureHereMarker();
        }
      },
      (err) => console.warn('Geolocation error:', err),
    );
  }

  onSearchNearby(): void {
    const realLoc = this.realUserLocation();
    if (realLoc) {
      this.searchCenter.set(realLoc);
      this.addressInput.set('');
      this.buildYoureHereMarker();
      this.performSearch(true);
      return;
    }
    if (!navigator.geolocation) {
      alert('Geolocation is not supported. Please enter an address.');
      return;
    }
    this.isSearching.set(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        this.realUserLocation.set(loc);
        this.searchCenter.set(loc);
        this.addressInput.set('');
        this.buildYoureHereMarker();
        this.performSearch(true);
      },
      () => {
        this.isSearching.set(false);
        alert('Unable to get your location. Please enable location services.');
      },
    );
  }

  onAddressInput(value: string): void {
    this.addressInput.set(value);
    if (!value.trim() || !this.autocompleteService) {
      this.showAutocomplete.set(false);
      return;
    }
    this.autocompleteService.getPlacePredictions(
      { input: value, types: ['address'] },
      (predictions: any[] | null) => {
        this.showAutocomplete.set(!!predictions?.length);
        this.autocompleteResults.set(predictions ?? []);
      },
    );
  }

  onSelectAutocomplete(prediction: any): void {
    this.addressInput.set(prediction.description);
    this.showAutocomplete.set(false);

    // Get coordinates for the selected place
    if (this.map && prediction.place_id) {
      try {
        const service = new google.maps.places.PlacesService(this.map);
        service.getDetails({
          placeId: prediction.place_id,
          fields: ['geometry']
        }, (place: any, status: any) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place.geometry?.location) {
            const loc = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            };
            this.searchCenter.set(loc);
            if (this.currentStep() === 'pickup_location') {
              this.buildPickupLocationMarker(loc, prediction.description);
            } else {
              console.log('[ServiceOptionPopup] Updated searchCenter from selection:', loc);
              this.performSearch(false);
            }
          } else {
            if (this.currentStep() === 'pickup_location') {
              this.searchCenter.set(null);
              this.toastr.showError('We could not resolve that pickup address.');
            } else {
              this.performSearch(false);
            }
          }
        });
      } catch (e) {
        console.error('[ServiceOptionPopup] Error geocoding selection:', e);
        this.performSearch(false);
      }
    } else {
      this.performSearch(false);
    }
  }

  onAddressSearch(): void {
    this.showAutocomplete.set(false);
    const searchText = this.addressInput().trim();
    if (!searchText) return;

    // Use geocoder to find the location if no selection from autocomplete was made
    try {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: searchText }, (results: any, status: any) => {
        if (status === 'OK' && results?.[0]?.geometry?.location) {
          const loc = {
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng()
          };
          this.searchCenter.set(loc);
          if (this.currentStep() === 'pickup_location') {
            this.buildPickupLocationMarker(loc, searchText);
          } else {
            this.buildYoureHereMarker();
            this.performSearch(false);
          }
        } else {
          if (this.currentStep() !== 'pickup_location') this.performSearch(false);
        }
      });
    } catch (e) {
      console.error('[ServiceOptionPopup] Geocoding failed:', e);
      this.performSearch(false);
    }
  }

  performSearch(useNearby: boolean): void {
    const searchText = this.addressInput().trim();
    const location = this.searchCenter();

    if (!searchText && !this.selectedServiceType() && !this.selectedSubType()) {
      alert('Please enter an address, select a service type, or use "Search nearby"');
      return;
    }

    this.businessResults.set([]);
    this.resultsCount.set(0);
    this.clearMapMarkers();
    this.isSearching.set(true);
    const params: Record<string, string> = {};

    if (searchText && !useNearby) params['search'] = searchText;
    if (this.selectedServiceType()?.service_type)
      params['service_type'] = this.selectedServiceType()!.service_type;
    if (this.selectedSubType()?.search_value)
      params['sub_service_type'] = this.selectedSubType()!.search_value;
    if (location) {
      params['lat'] = location.lat.toString();
      params['lng'] = location.lng.toString();
      params['radius'] = '5000';
    }
    params['pro_fields'] = 'true';

    this.httpClient
      .getWithParams<SearchResult>('search-business-places/text-search', params)
      .subscribe({
        next: (response) => {
          this.isSearching.set(false);
          this.hasSearched.set(true);
          if (response.success && Array.isArray(response.data)) {
            this.businessResults.set(response.data);
            this.resultsCount.set(response.count ?? response.data.length);
            this.updateMapMarkers();
          }
        },
        error: () => {
          this.isSearching.set(false);
          this.hasSearched.set(true);
        },        
      });
  }

  onReset(): void {
    this.resetSearchForm();
  }

  formatTime(hour?: number, minute?: number): string {
    if (hour === undefined) return '';

    const ampm = hour >= 12 ? 'PM' : 'AM';
    const formattedHour = hour % 12 || 12;
     const formattedMinute = String(minute ?? 0).padStart(2, '0');

    return `${formattedHour}:${formattedMinute} ${ampm}`;
  }

  getOpenStatusLine(business: BusinessResult): string {
    const hours = business.regularOpeningHours;
    if (!hours) return '';

    if (hours.openNow && hours.nextCloseTime) {
      try {
        const closeDate = new Date(hours.nextCloseTime);
        const timeStr = closeDate.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        return `Close ${timeStr.replace(':00', '')}`;
      } catch (e) { }
    } else if (!hours.openNow && hours.nextOpenTime) {
      try {
        const openDate = new Date(hours.nextOpenTime);
        const timeStr = openDate.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        return `Open ${timeStr.replace(':00', '')}`;
      } catch (e) { }
    }

    const periods = hours.periods;
    if (!periods?.length) return '';

    const today = new Date().getDay();
    const todayPeriod = periods.find(p => p.open?.day === today);
    if (!todayPeriod) return '';

    if (hours.openNow) {
      return `Close ${this.formatTime(todayPeriod.close?.hour, todayPeriod.close?.minute)}`;
    } else {
      return `Open ${this.formatTime(todayPeriod.open?.hour, todayPeriod.open?.minute)}`;
    }
  }

  getPriceDollarSigns(business: BusinessResult): string {
    if (business.priceLevel !== undefined) {
      return '$'.repeat(Math.max(1, business.priceLevel));
    }
    return '';
  }

  private resetSearchForm(): void {
    this.selectedServiceType.set(null);
    this.selectedSubType.set(null);
    this.addressInput.set('');
    this.availableSubTypes.set([]);
    this.businessResults.set([]);
    this.resultsCount.set(0);
    this.showAutocomplete.set(false);
    this.hasSearched.set(false);
    this.clearMapMarkers();
  }
  getBusinessDisplayName(b: BusinessResult): string {
    return b.displayName?.text ?? b.name ?? 'Business Name';
  }

  getBusinessInitial(b: BusinessResult): string {
    return (b.displayName?.text ?? b.name ?? 'A')[0].toUpperCase();
  }

  getBusinessAvatarBg(b: BusinessResult): string {
    return b.iconBackgroundColor ?? '#238EC6';
  }

  private escapeHtml(text: string): string {
    if (!text) return '';
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };
    return text.replace(/[&<>"']/g, (m) => map[m]);
  }
  getBusinessShortAddress(b: BusinessResult): string {
    if (!b.formattedAddress) return '';
    const parts = b.formattedAddress.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 3) {
      return parts.slice(1, -1).join(', ');
    }
    return b.formattedAddress;
  }

  formatRatingCount(n: number): string {
    return n.toLocaleString('en-US');
  }

  readonly iconMap: Record<string, string> = {
    laundry: 'laundry.svg',
    cleaning: 'cleaning.svg',
    pet_care: 'petcare.svg',
    handyman_moving: 'handyman.svg',
    photo_video: 'photo.svg',
    healthcare_homeaide: 'healthcare.svg',
    tutoring: 'tutoring.svg',
    personal_services: 'personal.svg',
    freelance_work: 'freelancer.svg',
    automotive_services: 'automotive.svg',
    event_services: 'catering.svg',
    health_beauty_services: 'beauty.svg',
    home_improvement: 'home-improvement.svg',
    business_services: 'business-service.svg',
    legal_services: 'legal.svg',
    yard_work: 'yard.svg',
  };

  getServiceIcon(type: ServiceType): string {
    return `assets/icons/fill-icons/${this.iconMap[type.service_type] ?? 'default.svg'}`;
  }

  private async initMap(): Promise<void> {
    if (this.mapInitialized || !this.mapContainer?.nativeElement) return;
    try {
      if (typeof google === 'undefined' || !google.maps) {
        await this.gmapService.api;
      }
      const center = this.searchCenter() ?? this.realUserLocation() ?? { lat: 40.7128, lng: -74.006 };

      this.map = new google.maps.Map(this.mapContainer.nativeElement, {
        center,
        zoom: 13,
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        styles: [
          { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
        ],
      });

      this.CardOverlay = class extends google.maps.OverlayView {
        private _el: HTMLDivElement;
        private _latlng: any;

        constructor(el: HTMLDivElement, latlng: any) {
          super();
          this._el = el;
          this._latlng = latlng;
        }

        onAdd(): void {
          this['getPanes']().floatPane.appendChild(this._el);
        }

        draw(): void {
          const pt = this['getProjection']().fromLatLngToDivPixel(this._latlng);
          if (pt) {
            this._el.style.left = `${pt.x}px`;
            this._el.style.top = `${pt.y}px`;
          }
        }

        onRemove(): void {
          this._el.parentNode?.removeChild(this._el);
        }
      };

      this.autocompleteService = new google.maps.places.AutocompleteService();
      this.mapInitialized = true;

      if (this.currentStep() === 'pickup_location') {
        const center = this.searchCenter() ?? this.realUserLocation();
        const address = this.addressInput().trim();
        if (center && address) {
          this.buildPickupLocationMarker(center, address);
        }
      } else if (this.businessResults().length > 0) {
        this.updateMapMarkers();
      } else if (this.realUserLocation() || this.searchCenter()) {
        this.updateMapMarkers(); // updateMapMarkers handles both types
      }
    } catch (e) {
      console.error('Google Maps init failed:', e);
    }
  }

  private buildMarkerCard(business: BusinessResult): HTMLDivElement {
    const name = this.getBusinessDisplayName(business);
    const cityStateZip = this.getBusinessCityStateZip(business);
    const photoUrl = this.getBusinessPhotoUrl(business);

    const card = document.createElement('div');
    card.style.cssText = [
      'position:absolute',
      'transform:translate(-50%, calc(-100% - 10px))',
      'background:#ffffff',
      'border-radius:5px',
      'padding:6px 10px 6px 6px',
      'display:flex',
      'align-items:center',
      'gap:8px',
      'cursor:pointer',
      "font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
      'min-width:142px',
      'max-width:142px',
      'white-space:nowrap',
      'pointer-events:auto',
    ].join(';');

    const initial = this.escapeHtml((business.displayName?.text ?? business.name ?? 'A')[0].toUpperCase());
    const bg = this.escapeHtml(business.iconBackgroundColor ?? '#238EC6');
    const rating = Number(business.average_rating || business.rating || 0);
    const count = business.total_rating_count || business.userRatingCount || 0;
    const escapedName = this.escapeHtml(name);
    const escapedPhotoUrl = this.escapeHtml(photoUrl);

    let starsHtml = '';
    const roundedRating = Math.round(rating);
    for (let i = 1; i <= 5; i++) {
      starsHtml += `<span style="color:#E59214;font-size:10px;">${i <= roundedRating ? '★' : '☆'}</span>`;
    }

    const thumbHtml = photoUrl
      ? `<div style="position:relative;width:25px;height:25px;flex-shrink:0;">
         <div style="
           width:25px;height:25px;border-radius:3px;
           background:${bg};color:#fff;font-size:15px;font-weight:700;
           display:flex;align-items:center;justify-content:center;
           position:absolute;inset:0;
         ">${initial}</div>
         <img src="${escapedPhotoUrl}"
           style="width:25px;height:25px;border-radius:3px;object-fit:cover;position:absolute;inset:0;"
           onerror="this.style.display='none'">
       </div>`
      : `<div style="
          width:25px;height:25px;border-radius:3px;flex-shrink:0;
          background:${bg};color:#fff;font-size:15px;font-weight:700;
          display:flex;align-items:center;justify-content:center;
        ">${initial}</div>`;

    const caret = `<div style="
    position:absolute;
    bottom:-7px;
    left:50%;
    transform:translateX(-50%);
    width:0;height:0;
    border-left:7px solid transparent;
    border-right:7px solid transparent;
    border-top:7px solid #ffffff;
    filter:drop-shadow(0 2px 1px rgba(0,0,0,0.08));
  "></div>`;

    card.innerHTML = `
    ${thumbHtml}
    <div style="min-width:0;flex:1;overflow:hidden;margin-left:8px;">
      <div style="
        font-size:12.5px;font-weight:700;color:#192A3E;
         white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
      ">${escapedName}</div>
      <div style="display:flex;align-items:center;gap:3px;margin-top:1px;">
        <span style="font-size:11px;font-weight:600;color:#4E4D4D;">${rating > 0 ? rating.toFixed(1) : '0'}</span>
        <div style="display:flex;">${starsHtml}</div>
        <span style="font-size:10px;color:#787E87;margin-left:1px;">(${count > 999 ? (count / 1000).toFixed(1) + 'k' : count})</span>
      </div>
    </div>
    ${caret}
  `;

    return card;
  }

  private teardownMap(): void {
    this.clearMapMarkers();
    this.map = null;
    this.CardOverlay = null;
    this.mapInitialized = false;
  }

  private clearMapMarkers(): void {
    this.markers.forEach((m) => {
      try { m.setMap(null); } catch { }
    });
    this.markers = [];

    if (this.youreHereOverlay) {
      try { this.youreHereOverlay.setMap(null); } catch { }
      this.youreHereOverlay = null;
    }

    if (this.pickupLocationOverlay) {
      try { this.pickupLocationOverlay.setMap(null); } catch { }
      this.pickupLocationOverlay = null;
    }
  }

  private updateMapMarkers(): void {
    if (!this.map || !this.CardOverlay) return;
    this.clearMapMarkers();

    const results = this.businessResults();
    if (!results.length) return;

    const bizBounds = new google.maps.LatLngBounds();
    results.forEach((business) => {
      const lat = business.location?.latitude;
      const lng = business.location?.longitude;
      if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) return;

      const latlng = new google.maps.LatLng(lat, lng);
      const cardEl = this.buildMarkerCard(business);
      const overlay = new this.CardOverlay(cardEl, latlng);
      overlay.setMap(this.map);
      this.markers.push(overlay);

      bizBounds.extend(latlng);
    });

    const finalBounds = new google.maps.LatLngBounds();
    if (!bizBounds.isEmpty()) {
      // Start with business bounds
      const ne = bizBounds.getNorthEast();
      const sw = bizBounds.getSouthWest();
      finalBounds.extend(ne);
      finalBounds.extend(sw);

    // Check if user location is "relevant" (within a reasonable distance or if searching nearby)
      const loc = this.searchCenter() ?? this.realUserLocation();
      if (loc) {
        if (google?.maps?.geometry?.spherical) {
          const center = bizBounds.getCenter();
          const dist = google.maps.geometry.spherical.computeDistanceBetween(
            center,
            new google.maps.LatLng(loc.lat, loc.lng)
          );
          // If within 50km, include user in bounds
          if (dist < 50000) {
            finalBounds.extend(new google.maps.LatLng(loc.lat, loc.lng));
          }
        } else {
          // Fallback: if geometry lib not loaded, just include user
          finalBounds.extend(new google.maps.LatLng(loc.lat, loc.lng));
        }
      }
    } else {
      const loc = this.searchCenter() ?? this.realUserLocation();
      if (loc) finalBounds.extend(new google.maps.LatLng(loc.lat, loc.lng));
    }

    if (!finalBounds.isEmpty()) {
      try {
        const ne = finalBounds.getNorthEast();
        const sw = finalBounds.getSouthWest();
        const literal = {
          north: ne.lat(),
          south: sw.lat(),
          east: ne.lng(),
          west: sw.lng()
        };

        if (Number.isFinite(literal.north) && Number.isFinite(literal.south) &&
          Number.isFinite(literal.east) && Number.isFinite(literal.west)) {
          console.log('[ServiceOptionPopup] fitting bounds literal:', literal);
          this.map.fitBounds(literal, 60);
          google.maps.event.addListenerOnce(this.map, 'idle', () => {
            if ((this.map.getZoom() ?? 15) > 15) this.map.setZoom(15);
          });
        }
      } catch (e) {
        console.error('[ServiceOptionPopup] Error fitting bounds:', e);
      }
    }

    this.buildYoureHereMarker();
  }

  getSubTypeIcon(subType: SubServiceType): string {
    if (subType.url && subType.url.trim().length > 0) {
      return subType.url;
    }
    const parent = this.selectedServiceType();
    const typeKey = parent?.service_type;
    const subKey = subType?.search_value;

    if (!typeKey || !subKey) {
      return 'assets/icons/home.png';
    }

    return (
      SERVICE_UI_MAP[typeKey]?.[subKey]?.icon ??
      'assets/icons/home.png'
    );
  }


  getDynamicHeader(): string {
    const parent = this.selectedServiceType();

    if (!parent) {
      return "Please select the service you're looking for";
    }

    return `Please select ${parent.label}`;
  }
  showContactMenu(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.currentMenu.toggle(event);
  }
  getPhotoUrl(photoReference: string | undefined): string {
    return photoReference?.startsWith('http') ? photoReference : '';
  }

  getBusinessPhotoUrl(business: BusinessResult): string {
    const primaryPhoto = business.image_url ?? business.photos?.[0]?.image_url;
    return this.getPhotoUrl(primaryPhoto);
  }
  isWheelchairAccessible(b: BusinessResult): boolean {
    const a = b.accessibilityOptions;
    if (!a) return false;
    return !!(
      a.wheelchairAccessibleEntrance ||
      a.wheelchairAccessibleParking ||
      a.wheelchairAccessibleRestroom ||
      a.wheelchairAccessibleSeating
    );
  }

  getBusinessStatusLabel(b: BusinessResult): string {
    if (!b.businessStatus) return '';
    return b.businessStatus === 'OPERATIONAL' ? 'OPEN' : b.businessStatus.replace(/_/g, ' ');
  }

  getBusinessStatusClass(b: BusinessResult): string {
    if (!b.businessStatus) return '';
    return b.businessStatus === 'OPERATIONAL' ? 'biz-status--open' : 'biz-status--closed';
  }
  getBusinessCityStateZip(b: BusinessResult): string {
    if (!b.formattedAddress) return '';
    const parts = b.formattedAddress.split(',').map(p => p.trim()).filter(Boolean);
    if (parts.length >= 4) {
      return `${parts[1]}, ${parts[2]}`;
    } else if (parts.length === 3) {
      return `${parts[1]}, ${parts[2]}`;
    } else if (parts.length === 2) {
      return parts[1];
    }
    return '';
  }

  private buildYoureHereMarker(): void {
    if (this.youreHereOverlay) {
      try { this.youreHereOverlay.setMap(null); } catch { }
      this.youreHereOverlay = null;
    }

    const realLoc = this.realUserLocation();
    const centerLoc = this.searchCenter();

    if (!this.map || !this.CardOverlay) return;

    // 1. Build "You're here" (the signpost) if it exists
    if (realLoc) {
      const latlng = new google.maps.LatLng(realLoc.lat, realLoc.lng);
      const el = document.createElement('div');
      el.style.cssText = [
        'position:absolute',
        'transform:translate(-50%, -100%)',
        'display:flex',
        'flex-direction:column',
        'align-items:center',
        'pointer-events:none',
        'z-index:1000',
      ].join(';');

      el.innerHTML = `
      <!-- Sign board -->
      <div style="
        background:#1a2035;
        color:#ffffff;
        border-radius:5px;
        padding:5px 7px;
        font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
        font-size:12px;
        font-weight:800;
        line-height:1.35;
        text-align:center;
        white-space:nowrap;
        box-shadow:0 4px 16px rgba(0,0,0,0.35);
        letter-spacing:0.2px;
      ">You're<br>here</div>

      <!-- Stem / pole -->
      <div style="
        width:4px;
        height:15px;
        background:#1a2035;
        border-radius:0 0 2px 2px;
      "></div>

      <!-- Tiny base circle at ground point -->
      <div style="
        width:8px;
        height:8px;
        background:#1a2035;
        border-radius:50%;
        margin-top:-1px;
      "></div>
    `;

      const YoureHereOverlay = class extends google.maps.OverlayView {
        private _el: HTMLDivElement;
        private _latlng: any;
        constructor(el: HTMLDivElement, latlng: any) {
          super();
          this._el = el;
          this._latlng = latlng;
        }
        onAdd(): void {
          this['getPanes']().floatPane.appendChild(this._el);
        }
        draw(): void {
          const projection = this['getProjection']();
          if (!projection) return;
          const pt = projection.fromLatLngToDivPixel(this._latlng);
          if (pt) {
            this._el.style.left = `${pt.x}px`;
            this._el.style.top = `${pt.y}px`;
          }
        }
        onRemove(): void {
          this._el.parentNode?.removeChild(this._el);
        }
      };

      this.youreHereOverlay = new YoureHereOverlay(el, latlng);
      this.youreHereOverlay.setMap(this.map);
    }

    // 2. Build "Search Center" (standard pin) if it's different from real location
    if (centerLoc && (!realLoc || realLoc.lat !== centerLoc.lat || realLoc.lng !== centerLoc.lng)) {
      const pinMarker = new google.maps.Marker({
        position: new google.maps.LatLng(centerLoc.lat, centerLoc.lng),
        map: this.map,
        title: 'Search Center',
        icon: {
          url: 'assets/icons/location-pin.svg',
          scaledSize: new google.maps.Size(32, 32),
          anchor: new google.maps.Point(16, 32)
        },
        zIndex: 999
      });
      this.markers.push(pinMarker);
    }
  }

  private buildPickupLocationMarker(loc: { lat: number; lng: number }, address: string): void {
    if (this.pickupLocationOverlay) {
      try { this.pickupLocationOverlay.setMap(null); } catch { }
      this.pickupLocationOverlay = null;
    }
    if (!this.map) return;

    const latlng = new google.maps.LatLng(loc.lat, loc.lng);
const safeAddress = this.escapeHtml(address);
    const el = document.createElement('div');
    el.style.cssText = [
      'position:absolute',
      'transform:translate(-50%, -100%)',
      'display:flex',
      'flex-direction:column',
      'align-items:center',
      'pointer-events:none',
      'z-index:1000',
    ].join(';');

    el.innerHTML = `
      <div style="
        background:#ffffff;
        border-radius:5px;
        padding:12px 14px;
        
        box-shadow:0 4px 20px rgba(0,0,0,0.22);
        min-width:174px;
        max-width:220px;
      ">
        <div style="font-size:12px;font-weight:600;color:#17B09A;margin-bottom:5px;">Pickup Location</div>
        <div style="font-size:14px;font-weight:600;color:#192A3E;">${safeAddress}</div>
      </div>
    `;

    const PickupOverlay = class extends google.maps.OverlayView {
      private _el: HTMLDivElement;
      private _latlng: any;
      constructor(el: HTMLDivElement, latlng: any) {
        super();
        this._el = el;
        this._latlng = latlng;
      }
      onAdd(): void { this['getPanes']().floatPane.appendChild(this._el); }
      draw(): void {
        const pt = this['getProjection']().fromLatLngToDivPixel(this._latlng);
        if (pt) {
          this._el.style.left = `${pt.x}px`;
          this._el.style.top = `${pt.y}px`;
        }
      }
      onRemove(): void { this._el.parentNode?.removeChild(this._el); }
    };

    this.pickupLocationOverlay = new PickupOverlay(el, latlng);
    this.pickupLocationOverlay.setMap(this.map);
    this.map.panTo(latlng);
    this.map.setZoom(15);
  }
}
